
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useProducts } from "@/hooks/useProducts";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useFactories } from "@/hooks/useFactories";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export interface VariantFormProps {
  variant?: any;
  onSubmit: (values: any) => Promise<boolean>;
  onCancel: () => void;
}

const formSchema = z.object({
  name: z.string().min(2, { message: "Variant name must be at least 2 characters." }),
  products: z.array(z.string()).default([]),
  factories: z.array(z.string()).default([]),
  image_url: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  add_custom_option: z.boolean().default(false),
  variants_order: z.number().min(0, { message: "Sort order must be 0 or greater." }),
  variant_monogram: z.object({
    allow_monogram: z.boolean().default(false),
    allow_text: z.boolean().default(false),
    font_styles: z.array(z.string()).default([]),
    allow_monogram_style: z.boolean().default(false),
    monogram_styles: z.array(z.string()).default([]),
    allow_monogram_color: z.boolean().default(false),
    monogram_colors: z.array(z.string()).default([]),
    allow_image: z.boolean().default(false),
  }).optional(),
  variant_group: z.array(z.object({
    group_number: z.number(),
    group_heading: z.string().optional(),
    selection_required: z.boolean().default(false),
    add_select_none: z.boolean().default(false),
  })).default([]),
});

export type VariantFormValues = z.infer<typeof formSchema>;

export function VariantForm({ variant, onSubmit, onCancel }: VariantFormProps) {
  const [imagePreview, setImagePreview] = useState<string | null>(variant?.image_url || null);
  const [isUploading, setIsUploading] = useState(false);
  const { products } = useProducts();
  const { factories, loading: factoriesLoading } = useFactories();

  const form = useForm<VariantFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: variant?.name || "",
      products: variant?.products || [],
      factories: variant?.factories || [],
      image_url: variant?.image_url || null,
      description: variant?.description || "",
      add_custom_option: variant?.add_custom_option || false,
      variants_order: variant?.variants_order || 0,
      variant_monogram: variant?.variant_monogram || {
        allow_monogram: false,
        allow_text: false,
        font_styles: [],
        allow_monogram_style: false,
        monogram_styles: [],
        allow_monogram_color: false,
        monogram_colors: [],
        allow_image: false,
      },
      variant_group: Array.isArray(variant?.variant_group) 
        ? variant.variant_group 
        : variant?.variant_group && Object.keys(variant.variant_group).length > 0
        ? [variant.variant_group]
        : [],
    },
  });

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }

    try {
      setIsUploading(true);
      
      // Create a file URL for preview
      const fileUrl = URL.createObjectURL(file);
      setImagePreview(fileUrl);
      
      // Here you would typically upload to your storage provider
      // For now, we're just using the file URL for display
      form.setValue("image_url", fileUrl);
      
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Failed to upload image");
    } finally {
      setIsUploading(false);
    }
  };

  const handleFormSubmit = async (values: VariantFormValues) => {
    const success = await onSubmit(values);
    if (success) {
      form.reset();
    }
  };

  const selectedProducts = form.watch("products") || [];
  const selectedFactories = form.watch("factories") || [];
  const allowText = form.watch("variant_monogram.allow_text");
  const selectedFontStyles = form.watch("variant_monogram.font_styles") || [];
  const allowMonogramStyle = form.watch("variant_monogram.allow_monogram_style");
  const selectedMonogramStyles = form.watch("variant_monogram.monogram_styles") || [];
  const allowMonogramColor = form.watch("variant_monogram.allow_monogram_color");
  const selectedMonogramColors = form.watch("variant_monogram.monogram_colors") || [];
  const variantGroupsRaw = form.watch("variant_group");
  const variantGroups = Array.isArray(variantGroupsRaw) ? variantGroupsRaw : [];

  const addGroup = () => {
    const newGroupNumber = variantGroups.length > 0 
      ? Math.max(...variantGroups.map(g => g.group_number)) + 1 
      : 1;
    
    const newGroup = {
      group_number: newGroupNumber,
      group_heading: "",
      selection_required: false,
      add_select_none: false,
    };
    
    form.setValue("variant_group", [...variantGroups, newGroup], {
      shouldValidate: true,
      shouldDirty: true
    });
  };

  const removeGroup = (index: number) => {
    const updatedGroups = variantGroups.filter((_, i) => i !== index);
    form.setValue("variant_group", updatedGroups, {
      shouldValidate: true,
      shouldDirty: true
    });
  };

  const fontStyleOptions = [
    "Arial",
    "Times New Roman",
    "Courier New",
    "Georgia",
    "Verdana",
    "Helvetica",
    "Comic Sans MS",
    "Impact"
  ];

  const monogramStyleOptions = ["Block", "Script", "Outline"];
  
  const monogramColorOptions = ["Black", "White", "Navy", "Red", "Gold", "Silver"];

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-4">
        <Tabs defaultValue="selection" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="selection">Selection</TabsTrigger>
            <TabsTrigger value="grouping">Grouping</TabsTrigger>
            <TabsTrigger value="monogram">Monogram</TabsTrigger>
          </TabsList>

          <TabsContent value="selection" className="space-y-4 mt-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Variant Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter variant name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="products"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Associated Products</FormLabel>
                  <FormControl>
                    <div className="border rounded-md p-1">
                      <ScrollArea className="h-[150px]">
                        <div className="p-3 space-y-2">
                          {products.length === 0 ? (
                            <p className="text-sm text-muted-foreground">No products available</p>
                          ) : (
                            products.map((product) => (
                              <div key={product.id} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`product-${product.id}`}
                                  checked={selectedProducts.includes(product.id)}
                                  onCheckedChange={(checked) => {
                                    const updatedProducts = checked
                                      ? [...selectedProducts, product.id]
                                      : selectedProducts.filter(id => id !== product.id);
                                    form.setValue("products", updatedProducts, {
                                      shouldValidate: true,
                                      shouldDirty: true
                                    });
                                  }}
                                />
                                <label
                                  htmlFor={`product-${product.id}`}
                                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                  {product.name}
                                </label>
                              </div>
                            ))
                          )}
                        </div>
                      </ScrollArea>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="factories"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Associated Factories</FormLabel>
                  <FormControl>
                    <div className="border rounded-md p-1">
                      <ScrollArea className="h-[150px]">
                        <div className="p-3 space-y-2">
                          {factoriesLoading ? (
                            <p className="text-sm text-muted-foreground">Loading factories...</p>
                          ) : factories.length === 0 ? (
                            <p className="text-sm text-muted-foreground">No factories available</p>
                          ) : (
                            factories.map((factory) => (
                              <div key={factory.id} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`factory-${factory.id}`}
                                  checked={selectedFactories.includes(factory.name)}
                                  onCheckedChange={(checked) => {
                                    const updatedFactories = checked
                                      ? [...selectedFactories, factory.name]
                                      : selectedFactories.filter(name => name !== factory.name);
                                    form.setValue("factories", updatedFactories, {
                                      shouldValidate: true,
                                      shouldDirty: true
                                    });
                                  }}
                                />
                                <label
                                  htmlFor={`factory-${factory.id}`}
                                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                  {factory.name}
                                </label>
                              </div>
                            ))
                          )}
                        </div>
                      </ScrollArea>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Enter variant description" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="variants_order"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sort Order</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      placeholder="Enter sort order (e.g., 1, 2, 3...)" 
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <Label htmlFor="image">Image</Label>
              <div className="flex items-center gap-4">
                {imagePreview && (
                  <div className="relative w-20 h-20 rounded-md overflow-hidden border border-border">
                    <img
                      src={imagePreview}
                      alt="Variant"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div>
                  <Input
                    id="image"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={isUploading}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Max file size: 5MB. Recommended size: 400x400px.
                  </p>
                </div>
              </div>
            </div>

            <FormField
              control={form.control}
              name="add_custom_option"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>
                      Add Custom Option
                    </FormLabel>
                    <p className="text-xs text-muted-foreground">
                      Enable the ability to add custom options for this variant
                    </p>
                  </div>
                </FormItem>
              )}
            />
          </TabsContent>

          <TabsContent value="grouping" className="space-y-4 mt-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-medium">Variant Groups</h3>
              <Button type="button" onClick={addGroup} size="sm">
                <span className="mr-1">+</span> Add Group
              </Button>
            </div>

            {variantGroups.length === 0 ? (
              <div className="text-center py-8 border rounded-md border-dashed">
                <p className="text-sm text-muted-foreground mb-2">No groups added yet</p>
                <Button type="button" onClick={addGroup} variant="outline" size="sm">
                  <span className="mr-1">+</span> Add First Group
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {variantGroups.map((group, index) => (
                  <div key={index} className="border rounded-lg p-4 space-y-4 relative">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-primary">Group #{group.group_number}</span>
                      </div>
                      {variantGroups.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeGroup(index)}
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        >
                          ×
                        </Button>
                      )}
                    </div>

                    <FormField
                      control={form.control}
                      name={`variant_group.${index}.group_heading`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Group Heading</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter group heading" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`variant_group.${index}.selection_required`}
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel>Selection Required</FormLabel>
                            <p className="text-xs text-muted-foreground">
                              Make selection mandatory for this group
                            </p>
                          </div>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`variant_group.${index}.add_select_none`}
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel>Add and Select None</FormLabel>
                            <p className="text-xs text-muted-foreground">
                              Add a "None" option and select it by default
                            </p>
                          </div>
                        </FormItem>
                      )}
                    />
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="monogram" className="space-y-4 mt-4">
            <FormField
              control={form.control}
              name="variant_monogram.allow_monogram"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Allow Monogram</FormLabel>
                    <p className="text-xs text-muted-foreground">
                      Enable monogram customization for this variant
                    </p>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="variant_monogram.allow_text"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                        if (!checked) {
                          form.setValue("variant_monogram.font_styles", [], {
                            shouldValidate: true,
                            shouldDirty: true
                          });
                        }
                      }}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Allow Text</FormLabel>
                    <p className="text-xs text-muted-foreground">
                      Allow customers to add text to monogram
                    </p>
                  </div>
                </FormItem>
              )}
            />

            {allowText && (
              <FormField
                control={form.control}
                name="variant_monogram.font_styles"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Font Styles</FormLabel>
                    <FormControl>
                      <div className="border rounded-md p-1">
                        <ScrollArea className="h-[150px]">
                          <div className="p-3 space-y-2">
                            {fontStyleOptions.map((font) => (
                              <div key={font} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`font-${font}`}
                                  checked={selectedFontStyles.includes(font)}
                                  onCheckedChange={(checked) => {
                                    const updatedFonts = checked
                                      ? [...selectedFontStyles, font]
                                      : selectedFontStyles.filter(f => f !== font);
                                    form.setValue("variant_monogram.font_styles", updatedFonts, {
                                      shouldValidate: true,
                                      shouldDirty: true
                                    });
                                  }}
                                />
                                <label
                                  htmlFor={`font-${font}`}
                                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                  style={{ fontFamily: font }}
                                >
                                  {font}
                                </label>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="variant_monogram.allow_monogram_style"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                        if (!checked) {
                          form.setValue("variant_monogram.monogram_styles", [], {
                            shouldValidate: true,
                            shouldDirty: true
                          });
                        }
                      }}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Allow Monogram Style</FormLabel>
                    <p className="text-xs text-muted-foreground">
                      Allow customers to choose monogram style
                    </p>
                  </div>
                </FormItem>
              )}
            />

            {allowMonogramStyle && (
              <FormField
                control={form.control}
                name="variant_monogram.monogram_styles"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Monogram Styles</FormLabel>
                    <FormControl>
                      <div className="border rounded-md p-1">
                        <ScrollArea className="h-[150px]">
                          <div className="p-3 space-y-2">
                            {monogramStyleOptions.map((style) => (
                              <div key={style} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`monogram-style-${style}`}
                                  checked={selectedMonogramStyles.includes(style)}
                                  onCheckedChange={(checked) => {
                                    const updatedStyles = checked
                                      ? [...selectedMonogramStyles, style]
                                      : selectedMonogramStyles.filter(s => s !== style);
                                    form.setValue("variant_monogram.monogram_styles", updatedStyles, {
                                      shouldValidate: true,
                                      shouldDirty: true
                                    });
                                  }}
                                />
                                <label
                                  htmlFor={`monogram-style-${style}`}
                                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                  {style}
                                </label>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="variant_monogram.allow_monogram_color"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                        if (!checked) {
                          form.setValue("variant_monogram.monogram_colors", [], {
                            shouldValidate: true,
                            shouldDirty: true
                          });
                        }
                      }}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Allow Monogram Color</FormLabel>
                    <p className="text-xs text-muted-foreground">
                      Allow customers to choose monogram color
                    </p>
                  </div>
                </FormItem>
              )}
            />

            {allowMonogramColor && (
              <FormField
                control={form.control}
                name="variant_monogram.monogram_colors"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Monogram Colors</FormLabel>
                    <FormControl>
                      <div className="border rounded-md p-1">
                        <ScrollArea className="h-[150px]">
                          <div className="p-3 space-y-2">
                            {monogramColorOptions.map((color) => (
                              <div key={color} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`monogram-color-${color}`}
                                  checked={selectedMonogramColors.includes(color)}
                                  onCheckedChange={(checked) => {
                                    const updatedColors = checked
                                      ? [...selectedMonogramColors, color]
                                      : selectedMonogramColors.filter(c => c !== color);
                                    form.setValue("variant_monogram.monogram_colors", updatedColors, {
                                      shouldValidate: true,
                                      shouldDirty: true
                                    });
                                  }}
                                />
                                <label
                                  htmlFor={`monogram-color-${color}`}
                                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                  {color}
                                </label>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="variant_monogram.allow_image"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Allow Image for Monogram</FormLabel>
                    <p className="text-xs text-muted-foreground">
                      Allow customers to upload images for monogram
                    </p>
                  </div>
                </FormItem>
              )}
            />
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={form.formState.isSubmitting || isUploading}>
            {variant ? "Update" : "Create"} Variant
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
